import { useState, useCallback, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useAttendance } from "@/hooks/useAttendance";
import { usePointRanking } from "@/hooks/usePointRanking";
import {
  searchKeywords,
} from "@/mocks/homeData";

const rankColors: Record<number, { bg: string; border: string; medal: string; text: string; bar: string }> = {
  1: { bg: "bg-yellow-50", border: "border-yellow-200", medal: "text-yellow-400", text: "text-yellow-600", bar: "#fbbf24" },
  2: { bg: "bg-slate-100", border: "border-slate-200", medal: "text-slate-400", text: "text-slate-500", bar: "#94a3b8" },
  3: { bg: "bg-orange-50", border: "border-orange-200", medal: "text-orange-400", text: "text-orange-500", bar: "#fb923c" },
};

export default function RightSidebar() {
  const { user, signOut } = useAuth();
  const { checkedToday, checking, streakCount, doCheckIn } = useAttendance();
  const { rankings, topThree, loading: rankingLoading } = usePointRanking(10);
  const navigate = useNavigate();
  const [searchTab, setSearchTab] = useState("실시간");
  const searchTabs = ["실시간", "일간", "주간"];
  const [showDomainModal, setShowDomainModal] = useState(false);

  // Login form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: "" });
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((message: string) => {
    // Clear previous timer
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToast({ show: true, message });
    toastTimerRef.current = setTimeout(() => {
      setToast({ show: false, message: "" });
    }, 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  const handleCheckIn = useCallback(async () => {
    if (checking || checkedToday) return;
    const result = await doCheckIn();
    if (result.success) {
      showToast(result.message);
    }
  }, [checking, checkedToday, doCheckIn, showToast]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setLoginError(error.message);
      }
      // Auth state will update via onAuthStateChange
    } catch {
      setLoginError("로그인 중 오류가 발생했습니다.");
    } finally {
      setLoginLoading(false);
    }
  };

  return (
    <>
      {/* Attendance toast */}
      {toast.show && (
        <div className="fixed bottom-6 right-6 z-[100] animate-[toastSlideUp_0.35s_ease-out]">
          <div className="flex items-center gap-3 bg-background-100 border border-background-300 rounded-lg px-4 py-3 shadow-lg min-w-[260px] max-w-[360px]">
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-500/15 flex-shrink-0">
              <i className="ri-check-fill text-accent-500 text-sm" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-foreground-700">출석체크 완료!</p>
              <p className="text-[11px] text-foreground-600 mt-0.5">{toast.message}</p>
            </div>
            <button
              onClick={() => setToast({ show: false, message: "" })}
              className="w-5 h-5 flex items-center justify-center text-foreground-500 hover:text-foreground-700 transition-colors flex-shrink-0 cursor-pointer"
            >
              <i className="ri-close-line text-xs" />
            </button>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-3">
        {/* 로그인 / 프로필 */}
        <div className="bg-background-100 rounded-lg border border-background-500 overflow-hidden transition-all">
          <div className="flex items-center gap-2 px-3 py-2 md:py-2.5 border-b border-background-300">
            <h3 className="text-[11px] md:text-xs font-bold text-foreground-700">
              {user ? "내 프로필" : "월드컵티비 시즌2 로그인"}
            </h3>
          </div>
          <div className="px-3 py-3">
            {user ? (
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-user-fill text-primary-400 text-lg" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-foreground-700 truncate">
                      {user.user_metadata?.nickname || user.email?.split("@")[0] || "회원"}
                    </p>
                    <p className="text-[10px] text-foreground-600 truncate">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to="/profile"
                    className="flex-1 py-1.5 text-[11px] text-center bg-background-200 text-foreground-600 rounded-md hover:bg-background-300 transition-colors whitespace-nowrap"
                  >
                    내 정보
                  </Link>
                  <button
                    onClick={handleCheckIn}
                    disabled={checking || checkedToday}
                    className={`flex-1 py-1.5 text-[11px] text-center rounded-md transition-colors whitespace-nowrap cursor-pointer ${
                      checkedToday
                        ? "bg-accent-100/80 text-accent-700"
                        : "bg-accent-500 text-foreground-50 hover:bg-accent-600"
                    }`}
                  >
                    {checking ? "처리 중..." : checkedToday ? `D+${streakCount}` : "출석체크"}
                  </button>
                  <button
                    onClick={signOut}
                    className="flex-1 py-1.5 text-[11px] text-center bg-red-500/20 text-red-400 rounded-md hover:bg-red-500/30 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    로그아웃
                  </button>
                </div>
              </div>
            ) : (
              <>
                <form onSubmit={handleLogin} className="space-y-2">
                  {loginError && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2 text-[10px] text-red-400">
                      {loginError}
                    </div>
                  )}
                  <div className="flex items-center gap-2 bg-background-200 rounded-md px-3 py-2">
                    <span className="w-4 h-4 flex items-center justify-center text-foreground-600 flex-shrink-0">
                      <i className="ri-user-line" />
                    </span>
                    <input
                      placeholder="이메일"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="bg-transparent text-xs text-foreground-700 placeholder-foreground-500 w-full outline-none"
                      type="email"
                      required
                    />
                  </div>
                  <div className="flex items-center gap-2 bg-background-200 rounded-md px-3 py-2">
                    <span className="w-4 h-4 flex items-center justify-center text-foreground-600 flex-shrink-0">
                      <i className="ri-lock-line" />
                    </span>
                    <input
                      placeholder="비밀번호"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="bg-transparent text-xs text-foreground-700 placeholder-foreground-500 w-full outline-none"
                      type="password"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loginLoading}
                    className="w-full py-2 bg-primary-500 text-white text-xs font-bold rounded-md hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
                  >
                    {loginLoading ? "로그인 중..." : "로그인"}
                  </button>
                  {/* honeypot */}
                  <div className="hp-field">
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                    />
                  </div>
                </form>
                <div className="flex items-center justify-between mt-2">
                  <span className="flex items-center gap-1 text-[10px] text-foreground-600">
                    <i className="ri-checkbox-circle-line" />
                    비밀번호저장
                  </span>
                  <Link
                    to="/register"
                    className="flex items-center gap-1 text-[10px] text-foreground-600 hover:text-primary-400 transition-colors"
                  >
                    <i className="ri-user-add-line" />
                    회원가입
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>

        {/* 인기 검색어 */}
        <div className="bg-background-100 rounded-lg border border-background-500 overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 md:py-2.5 border-b border-background-300">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center text-red-400">
                <i className="ri-fire-fill" />
              </span>
              <h3 className="text-[11px] md:text-xs font-bold text-foreground-700">오늘의 인기 검색어</h3>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[8px] text-foreground-600 bg-background-200 px-1 py-0.5 rounded-full">샘플</span>
              <span className="text-[9px] text-foreground-600 bg-background-200 px-1.5 py-0.5 rounded-full">7. 1. 기준</span>
            </div>
          </div>
          <div className="flex items-center gap-1 px-2 py-2 border-b border-background-300">
            {searchTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setSearchTab(tab)}
                className={`px-2 py-0.5 text-[10px] rounded whitespace-nowrap transition-colors cursor-pointer ${
                  searchTab === tab
                    ? "bg-primary-500/20 text-primary-400 font-bold"
                    : "text-foreground-600 hover:text-foreground-600"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="px-3 py-2">
            <div className="space-y-1">
              {searchKeywords.map((item) => (
                <button
                  key={item.rank}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded-md transition-all group cursor-pointer text-left hover:bg-background-200/70"
                >
                  <span className={`w-6 h-6 flex items-center justify-center flex-shrink-0 ${
                    item.rank === 1 ? "text-yellow-400" : item.rank === 2 ? "text-slate-300" : item.rank === 3 ? "text-orange-400" : "text-foreground-600"
                  }`}>
                    {item.rank <= 3 ? (
                      <i className="ri-medal-fill text-base" />
                    ) : (
                      <span className="text-[11px] font-bold">{item.rank}</span>
                    )}
                  </span>
                  <span className="flex-1 text-[11px] transition-colors truncate text-foreground-600 group-hover:text-foreground-700">
                    {item.keyword}
                  </span>
                  <span className="text-[9px] text-foreground-600 flex-shrink-0 hidden sm:inline">{item.count}회</span>
                  <span className="flex items-center gap-0.5 flex-shrink-0">
                    {item.trend === "up" && (
                      <span className="text-red-400 flex items-center gap-0.5">
                        <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-up-fill" /></span>
                        <span className="text-[9px]">상승</span>
                      </span>
                    )}
                    {item.trend === "down" && (
                      <span className="text-blue-400 flex items-center gap-0.5">
                        <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-down-fill" /></span>
                        <span className="text-[9px]">하락</span>
                      </span>
                    )}
                    {item.trend === "same" && (
                      <span className="text-foreground-600 flex items-center gap-0.5">
                        <span className="w-3 h-3 flex items-center justify-center"><i className="ri-subtract-line" /></span>
                        <span className="text-[9px]">유지</span>
                      </span>
                    )}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="px-3 py-2 text-[10px] text-foreground-600 border-t border-background-300 text-center">
            <button className="text-foreground-600 hover:text-primary-400 transition-colors cursor-pointer whitespace-nowrap">
              전체 검색어 순위 보기 &gt;
            </button>
          </div>
        </div>

        {/* 사칭 사이트 주의 */}
        <div className="bg-red-950/30 rounded-lg border border-red-500/20 overflow-hidden">
          <div className="px-3 py-3 md:py-3.5">
            <div className="flex items-start gap-2.5">
              <span className="w-7 h-7 flex items-center justify-center rounded-full bg-red-500/15 flex-shrink-0 mt-0.5">
                <i className="ri-alarm-warning-fill text-red-400 text-sm" />
              </span>
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-bold text-red-400 mb-1">
                  사칭 사이트 주의 안내
                </h4>
                <p className="text-[10px] text-foreground-600 leading-relaxed">
                  공식 도메인은 수시로 변경됩니다.
                  <br />
                  반드시 공식 채널에서 확인하세요.
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowDomainModal(true)}
              className="mt-2.5 w-full py-2 text-[11px] font-bold bg-red-500/15 text-red-400 border border-red-500/20 rounded-md hover:bg-red-500/25 hover:text-red-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="flex items-center justify-center gap-1.5">
                <i className="ri-shield-check-line" />
                공식 주소 확인하기
              </span>
            </button>
          </div>
        </div>

        {/* 포인트 랭킹 */}
        <div className="bg-background-100 rounded-lg border border-background-500 overflow-hidden">
          <div className="flex items-center justify-between px-3 md:px-4 py-2.5 md:py-3 border-b border-background-300">
            <div className="flex items-center gap-2">
              <span className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center text-yellow-500">
                <i className="ri-trophy-line" />
              </span>
              <h2 className="text-xs md:text-sm font-bold text-foreground-700">포인트 랭킹</h2>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
              <span className="text-[9px] md:text-[10px] text-foreground-600">LIVE</span>
              {user && (
                <button
                  onClick={handleCheckIn}
                  disabled={checking || checkedToday}
                  className={`text-[10px] md:text-xs ml-1 px-2 py-0.5 rounded transition-colors whitespace-nowrap cursor-pointer ${
                    checkedToday
                      ? "bg-accent-100/80 text-accent-700"
                      : "bg-accent-500 text-foreground-50 hover:bg-accent-600"
                  }`}
                >
                  {checking ? "처리 중..." : checkedToday ? `D+${streakCount}` : "출석체크"}
                </button>
              )}
              {!user && (
                <Link
                  to="/login"
                  className="text-[10px] md:text-xs text-foreground-600 hover:text-primary-400 transition-colors ml-1 cursor-pointer whitespace-nowrap"
                >
                  출석체크 &gt;
                </Link>
              )}
            </div>
          </div>
          <div className="px-3 md:px-4 py-2">
            {rankingLoading ? (
              <div className="py-4 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : topThree.length > 0 ? (
              <>
                {/* Top 3 */}
                <div className="grid grid-cols-3 gap-3 mb-3 pb-3 border-b border-background-300">
                  {topThree.map((rankUser, idx) => {
                    const colors = rankColors[rankUser.rank] || rankColors[3];
                    const isFirst = idx === 1 || (topThree.length === 1 && idx === 0) || (topThree.length === 2 && idx === 0);
                    // Reorder: rank 2, rank 1, rank 3 (crown at center)
                    const displayOrder = topThree.length >= 3
                      ? [1, 0, 2].map(i => topThree[i]).filter(Boolean)
                      : topThree;
                    const displayUser = displayOrder[idx];
                    const displayColors = displayUser ? (rankColors[displayUser.rank] || rankColors[3]) : colors;
                    const isFirstDisplay = displayUser?.rank === 1;

                    if (!displayUser) return null;

                    return (
                      <div key={displayUser.id} className="flex flex-col items-center gap-1">
                        <div className={`${isFirstDisplay ? "w-10 h-10 md:w-11 md:h-11" : "w-8 h-8 md:w-9 md:h-9"} rounded-full flex items-center justify-center relative ${displayColors.bg}`}>
                          <i className={`ri-user-fill ${isFirstDisplay ? "text-yellow-500 text-base" : displayColors.medal + " text-sm"}`} />
                          {isFirstDisplay && (
                            <span className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-vip-crown-fill text-yellow-400 text-xs" />
                            </span>
                          )}
                        </div>
                        <span className={`text-[9px] font-bold truncate max-w-full text-center ${isFirstDisplay ? "text-foreground-700" : "text-foreground-600"}`}>
                          {displayUser.nickname || "익명"}
                        </span>
                        <div className={`flex items-center gap-0.5 rounded-full px-2 py-0.5 ${displayColors.bg} border ${displayColors.border}`}>
                          <span className="w-3 h-3 flex items-center justify-center">
                            <i className={`ri-medal-fill ${displayColors.medal} text-[10px]`} />
                          </span>
                          <span className={`text-[9px] font-bold ${displayColors.text}`}>{displayUser.points}P</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Rankings 4+ */}
                <div className="space-y-1">
                  {rankings.slice(3).map((rankUser) => {
                    const colors = rankColors[rankUser.rank] || {
                      text: "text-foreground-600",
                      bar: rankUser.rank <= 5 ? "#22c55e" : rankUser.rank <= 8 ? "#0ea5e9" : "#94a3b8",
                      medal: "text-foreground-600",
                    };
                    const maxPoints = rankings[0]?.points || 1;
                    const barWidth = Math.max((rankUser.points / maxPoints) * 100, 5);

                    return (
                      <div key={rankUser.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors hover:bg-background-200/50">
                        <span className="w-5 text-center text-[10px] font-bold flex-shrink-0 text-foreground-600">
                          {rankUser.rank}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 mb-0.5">
                            <span className="text-[10px] font-medium truncate text-foreground-600">
                              {rankUser.nickname || "익명"}
                            </span>
                          </div>
                          <div className="w-full bg-background-200 rounded-full h-1">
                            <div
                              className="h-1 rounded-full transition-all"
                              style={{ width: `${barWidth}%`, backgroundColor: colors.bar }}
                            />
                          </div>
                        </div>
                        <span className="text-[10px] font-bold flex-shrink-0 text-foreground-600">{rankUser.points}P</span>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="py-6 text-center">
                <p className="text-[11px] text-foreground-600">아직 랭킹 데이터가 없습니다</p>
              </div>
            )}
            <div className="mt-3 pt-2 border-t border-background-300 text-center">
              <Link
                to="/guide/gift"
                className="w-full py-2 text-[10px] md:text-xs bg-accent-500 text-foreground-50 rounded-md hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap font-medium inline-block"
              >
                <i className="ri-shopping-bag-3-line mr-1" />
                포인트샵에서 상품 교환하기
              </Link>
            </div>
          </div>
        </div>

        {/* 공식 주소 확인 모달 */}
        {showDomainModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowDomainModal(false)}
            />
            <div className="relative bg-background-100 rounded-xl border border-background-300 shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in">
              <div className="bg-red-500 px-5 py-4">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-white">
                    <i className="ri-shield-check-fill" />
                  </span>
                  <h3 className="text-sm font-bold text-white">공식 도메인 안내</h3>
                </div>
              </div>
              <div className="p-5">
                <div className="bg-background-200 rounded-lg border border-background-300 p-4 text-center">
                  <p className="text-xs text-foreground-600 mb-2">월드컵티비 시즌2 공식 주소는</p>
                  <a
                    href="https://wdctv24.com"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="block text-xl font-black text-primary-500 hover:text-primary-400 transition-colors tracking-wide"
                  >
                    wdctv24.com
                  </a>
                  <p className="text-[10px] text-foreground-600 mt-3 leading-relaxed">
                    사칭 사이트에 주의하세요!
                    <br />
                    반드시 공식 도메인을 확인하고 접속하시기 바랍니다.
                  </p>
                </div>
                <div className="flex items-center gap-2 mt-4">
                  <a
                    href="https://wdctv24.com"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex-1 py-2 bg-primary-500 text-foreground-50 text-xs font-bold rounded-lg hover:bg-primary-600 transition-colors text-center whitespace-nowrap cursor-pointer"
                  >
                    바로가기
                  </a>
                  <button
                    onClick={() => setShowDomainModal(false)}
                    className="flex-1 py-2 bg-background-200 text-foreground-600 text-xs font-medium rounded-lg hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    닫기
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}